import React, { JSX, useContext, useEffect, useState } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { colors } from "constants/colors";
import { assetsRoot } from "config";
import { LogContext } from "context/log";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { FALL_FOR_FLAVOR_CARD_IMAGE } from "constants/pdpProducts";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";
import { SmartButton } from "components/SmartCommerce";
import { getProductUpc } from "constants/commerce";
import { useCommerceCollectionView } from "hooks/useCommerceAnalytics";

import st from "./drinks.module.scss";

// Replica of drinkpoppi.com/collections/drinks at the same URL, rebuilt from
// the live page: per-product gradient/name/button tokens read from the
// rendered DOM, arch-card anatomy pixel-verified (dome shows the dark "to"
// colour at the top fading to "from" at the bottom, circle-cropped can
// render, full-width pill button with the offset shadow). The filter chips
// replicate the live facets — product type plus the #classics/#fruity tags,
// whose membership was captured by exercising the live filters. Product
// purchase controls are hydrated by the production SmartCommerce config.

const A = `${assetsRoot}/poppi/collections`;

interface DrinkProduct {
  slug: string;
  name: string;
  from: string;
  to: string;
  type: "single" | "pack";
  tags: string[];
  limited?: boolean;
  buyNow?: boolean;
  // new-campaign card art still served from public/ (no CDN mirror yet)
  img?: string;
}

export const PRODUCTS: DrinkProduct[] = [
  {
    slug: "sour-apple",
    name: "Sour Apple",
    from: "#c1f22b",
    to: "#1c7a24",
    type: "single",
    tags: ["fruity"],
    img: `${assetsRoot}/poppi/sour-apple/can.png`,
  },
  {
    slug: "shirley-temple",
    name: "Shirley Temple",
    from: "#f26b6b",
    to: "#8e0011",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "orange-cream",
    name: "Orange Cream",
    from: "#ffe6d1",
    to: "#e15505",
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "strawberry-lemon",
    name: "Strawberry Lemon",
    from: colors.magenta,
    to: colors.yellow,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "wild-berry-12oz",
    name: "Wild Berry",
    from: colors.cyan,
    to: colors.purple,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "lemon-lime",
    name: "Lemon Lime",
    from: "#137c21",
    to: colors.yellow,
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "cream-soda",
    name: "Cream Soda",
    from: "#0667be",
    to: "#e9e4c5",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "rootbeer",
    name: "Root Beer",
    from: "#7c2e3d",
    to: "#f9e26d",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "alpine-blast",
    name: "Alpine Blast",
    from: "#45ce0d",
    to: "#0031a7",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "doc-pop",
    name: "Doc Pop",
    from: "#f66ff8",
    to: "#a40e56",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "orange",
    name: "Orange",
    from: colors.orange,
    to: colors.cream,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "cherry-cola",
    name: "Cherry Cola",
    from: "#700058",
    to: "#fd303a",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "ginger-lime",
    name: "Ginger Lime",
    from: colors.lime,
    to: colors.blue,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "raspberry-rose",
    name: "Raspberry Rose",
    from: colors.pink,
    to: colors.red,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "grape",
    name: "Grape",
    from: "#8725c3",
    to: colors.lime,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "cherry-limeade",
    name: "Cherry Limeade",
    from: "#d10119",
    to: colors.lime,
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "watermelon",
    name: "Watermelon",
    from: "#6bff4f",
    to: "#f61f6c",
    type: "single",
    tags: ["fruity"],
  },
  {
    slug: "classic-cola",
    name: "Classic Cola",
    from: "#e10600",
    to: "#ffd4c9",
    type: "single",
    tags: ["classics"],
  },
  {
    slug: "fall-for-flavor",
    name: "Fall for Flavor",
    from: "#cafe26",
    to: "#ff7500",
    type: "pack",
    tags: [],
    img: FALL_FOR_FLAVOR_CARD_IMAGE,
  },
  {
    slug: "major-mix",
    name: "Major Mix",
    from: "#8e0011",
    to: "#f26b6b",
    type: "pack",
    tags: [],
  },
  {
    slug: "crowd-pleasers",
    name: "Crowd Pleasers",
    from: colors.purple,
    to: colors.cyan,
    type: "pack",
    tags: [],
  },
  {
    slug: "functional-favorites",
    name: "Fun Favorites",
    from: colors.yellow,
    to: colors.magenta,
    type: "pack",
    tags: [],
  },
  {
    slug: "short-list",
    name: "Short List",
    from: colors.lime,
    to: "#d10119",
    type: "pack",
    tags: [],
  },
  {
    slug: "trend-setters",
    name: "Trend Setters",
    from: colors.red,
    to: colors.pink,
    type: "pack",
    tags: [],
  },
  {
    slug: "classics",
    name: "Classics",
    from: "#a40e56",
    to: "#f66ff8",
    type: "pack",
    tags: [],
  },
];

const CHIPS = [
  { key: "single", label: "single flavor" },
  { key: "pack", label: "variety pack" },
  { key: "classics", label: "#classics" },
  { key: "fruity", label: "#fruity" },
];

// "we flex on flavor" rides a wave path on live (SVG textPath, Recoleta 130).
function HeroArc(): JSX.Element {
  return (
    <svg
      className={st.heroArc}
      viewBox="0 0 1440 450"
      fill="none"
      aria-hidden="true"
    >
      <path
        id="drinks-flex-path"
        fill="none"
        d="M0 297.554C460.88 125.568 580.43 320.091 842.94 373.473C1154.19 436.774 1324.8 325.12 1440 230.463"
      />
      <text>
        <textPath href="#drinks-flex-path" startOffset="19%">
          we flex on flavor
        </textPath>
      </text>
    </svg>
  );
}

// The magenta band + yellow ribbon divider lifted from the live page.
function RibbonWave(): JSX.Element {
  return (
    <svg
      className={st.ribbon}
      viewBox="0 0 1443 476"
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 0H1443V207.366C1327.19 301.34 1155.37 409.172 844.709 346.477C770.022 331.405 706.843 305.058 644.142 278.91C485.702 212.836 330.314 148.035 0 269.642V0Z"
        fill={colors.magenta}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1443 221.287L1443 201.847C1327.6 296.303 1156.2 406.216 844.407 343.055C770.003 327.982 707.063 301.635 644.6 275.487C486.297 209.22 331.052 144.233 6.77313e-05 267.293L0 476H0.000342132C329.868 243.994 593.324 312.566 753.508 354.258C787.916 363.214 817.558 370.929 842.071 374.159C965.217 390.386 1190.18 413.693 1443 221.287Z"
        fill={colors.yellow}
      />
    </svg>
  );
}

export default function DrinksCollection(): JSX.Element {
  const { useLogPageView } = useContext(LogContext);
  const [active, setActive] = useState<ReadonlySet<string>>(new Set());

  // Live theming: magenta bar, yellow links, white logo; the bar casts live's
  // soft secondary-coloured glow (drop-shadow 0 4px 32px). Footer keeps its
  // yellow text and drops the newsletter form, like the other replica pages.
  useEffect(() => {
    const body = document.body;
    body.style.setProperty("--color-primary", colors.magenta);
    body.style.setProperty("--logo-color", "#ffffff");
    body.style.setProperty("--header-shadow", `0 4px 32px ${colors.yellow}`);
    body.style.setProperty("--page-top", "0px");
    body.style.setProperty("--footer-text", colors.yellow);
    return () => {
      body.style.removeProperty("--color-primary");
      body.style.removeProperty("--logo-color");
      body.style.removeProperty("--header-shadow");
      body.style.removeProperty("--page-top");
      body.style.removeProperty("--footer-text");
    };
  }, []);

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.collection,
    pageCategory: "drinks",
    pageTitle: `Drinks – ${META_TITLE}`,
    id: generatePageId("collections-drinks-page"),
  });
  useCommerceCollectionView();

  const toggle = (key: string) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Shopify-facet semantics: OR within a filter group, AND across groups.
  const types = ["single", "pack"].filter((t) => active.has(t));
  const tags = ["classics", "fruity"].filter((t) => active.has(t));
  const visible = PRODUCTS.filter(
    (p) =>
      (types.length === 0 || types.includes(p.type)) &&
      (tags.length === 0 || p.tags.some((t) => tags.includes(t)))
  );

  return (
    <div className={st.page}>
      <Head>
        <title>{`Drinks – ${META_TITLE}`}</title>
        <link
          rel="canonical"
          href={`${CANONICAL_URL_BASE}/collections/drinks`}
        />
        <meta
          name="description"
          content="we flex on flavor — shop every poppi prebiotic soda flavor and variety pack."
          key="description"
        />
      </Head>

      {/* ---------------- HERO (magenta + yellow ribbon + arched title) ---------------- */}
      <section className={st.hero}>
        <div className={st.heroBand} />
        <RibbonWave />
        <HeroArc />
      </section>

      {/* ---------------- filter chips (live facets) ---------------- */}
      <div className={st.chips} role="group" aria-label="filter products">
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`${st.chip} ${active.has(c.key) ? st.chipActive : ""}`}
            aria-pressed={active.has(c.key)}
            onClick={() => toggle(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* ---------------- product grid ---------------- */}
      <div className={st.grid}>
        {visible.map((p) => (
          <div className={st.card} key={p.slug}>
            <Link
              href={`/products/${p.slug}`}
              className={st.cardLink}
              aria-label={`poppi ${p.name}`}
            >
              <span
                className={st.arch}
                style={{
                  background: `linear-gradient(0deg, ${p.from} 0%, ${p.to} 100%)`,
                }}
              />
              <span
                className={`${st.can} ${p.type === "pack" ? st.canPack : ""}`}
              >
                {p.type === "pack" ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={p.img ?? `${A}/${p.slug}.webp`}
                    alt={`poppi ${p.name}`}
                    className={st.packImage}
                    loading="lazy"
                  />
                ) : (
                  <Image
                    unoptimized
                    src={p.img ?? `${A}/${p.slug}.webp`}
                    alt={`poppi ${p.name}`}
                    fill
                    sizes="300px"
                    style={{ objectFit: "cover", objectPosition: "50% 0%" }}
                  />
                )}
              </span>
              <h4 className={st.name} style={{ color: p.to }}>
                {p.name}
              </h4>
            </Link>
            {p.limited && (
              <span className={st.limited}>
                <svg viewBox="0 0 100 100" aria-hidden="true">
                  <polygon
                    points="50,0 59,14 75,7 76,24 93,24 86,39 100,50 86,61 93,76 76,76 75,93 59,86 50,100 41,86 25,93 24,76 7,76 14,61 0,50 14,39 7,24 24,24 25,7 41,14"
                    fill={colors.yellow}
                  />
                </svg>
                <i>
                  limited
                  <br />
                  edition
                </i>
              </span>
            )}
            {getProductUpc(p.slug) && (
              <SmartButton
                upc={getProductUpc(p.slug) as string}
                productName={p.name}
                className={st.cardBtn}
                style={
                  {
                    "--card-primary": p.from,
                    "--card-secondary": p.to,
                    "--color-primary": p.from,
                    "--color-secondary": p.to,
                  } as React.CSSProperties
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
